import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Backdrop,
  Fade,
  Modal,
} from "@mui/material";
import { API_ENDPOINT } from "../../../services/api/resources";
import axios from "axios";
import { useSelector } from "react-redux";
import BulkVerificationTable from "./VendorVerificationTable";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import classes from "./VendorVerification.module.css";

const VendorVerificationPartial = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const [user_token] = useState(loginData?.user?.token);

  const [documentType, setDocumentType] = useState("pan");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef();

  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [bulkRecords, setBulkRecords] = useState([]);
  const [paginationData, setPaginationData] = useState(null);
  const [tableProps, setTableProps] = useState({
    isLoading: false,
    errorMessage: "",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  const fetch_all_bulk_verification = async ({
    search = searchQuery,
    filter = filterQuery,
    page = pageNumber,
  } = {}) => {
    try {
      setTableProps((s) => ({ ...s, isLoading: true, errorMessage: "" }));

      const payload = {
        search: (search || "").trim(),
        filter: filter || "",
        page: Number(page) || 1,
      };

      const res = await axios.post(API_ENDPOINT.fetch_all_bulk_verification, payload);
      const responseData = res?.data ?? {};

      const records = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.records)
          ? responseData.records
          : Array.isArray(responseData?.results)
            ? responseData.results
            : [];

      setBulkRecords(records);

      const page_count = Number(responseData?.page_count) || 1;
      const current_page_count = Number(responseData?.current_page_count) || records.length;
      const total_count = Number(responseData?.total_count ?? responseData?.total_items) || records.length;

      setPaginationData({
        total_count,
        page_count,
        current_page_count,
      });
    } catch (err) {
      console.error(err);
      setTableProps((s) => ({
        ...s,
        errorMessage: "Failed to load verification data.",
      }));
    } finally {
      setTableProps((s) => ({ ...s, isLoading: false }));
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      fetch_all_bulk_verification();
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterQuery, pageNumber]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) setFile(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async () => {
    if (!file || !documentType) {
      alert("Please select a document type and upload a file.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", documentType);
    formData.append("buyer_token", user_token);

    try {
      await axios.post(
        API_ENDPOINT.bulk_document_verification,
        formData
      );
      setSuccessOpen(true);
      setFile(null);
      setPageNumber(1);
      fetch_all_bulk_verification({ page: 1 });
    } catch (error) {
      alert("Verification failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSampleDownload = () => {
    const sampleDataMap = {
      pan: ["VendorMail", "PAN Number", "VendorCode"],
      gst: ["VendorMail", "GSTNUMBER", "VendorCode"],
      bank: ["VendorMail", "Bank_Account", "IFSC", "VendorCode"],
      msme: ["VendorMail", "Udyam", "VendorCode"],
      mca: ["VendorMail", "CIN", "VendorCode"],
    };

    const selectedHeaders = sampleDataMap[documentType];

    if (!selectedHeaders) {
      alert("Please select a document type first.");
      return;
    }

    const csvContent = selectedHeaders.join(",") + "\n"; // Add one empty row
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    const downloadKey = documentType === "mca" ? "cin" : documentType;
    link.setAttribute("download", `sample_${downloadKey}_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterOptions = useMemo(
    () => [
      { value: "", label: "All" },
      { value: "pan", label: "PAN" },
      { value: "gst", label: "GST" },
      { value: "bank", label: "Bank" },
      { value: "msme", label: "MSME" },
      { value: "mca", label: "CIN" },
    ],
    []
  );

  return (
    <div className={classes.users_tab_wrapper}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className={classes.users_topHeader}>
        <div className={`${classes.lhs} `}>
          <HeaderTitle titleText="Advance Onboarding" customclass={classes.dashHead} />
        </div>
      </div>

      <Box sx={{ p: 2 }}>
        {/* Radio Buttons */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel
            component="legend"
            sx={{ fontSize: "14px", fontWeight: "bold" }}
          >
            Select the Verification Type
          </FormLabel>
          <RadioGroup
            row
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            sx={{ fontSize: "12px" }}
          >
            <FormControlLabel
              value="pan"
              control={<Radio />}
              label={<Typography sx={{ fontSize: "14px" }}>PAN</Typography>}
            />
            <FormControlLabel
              value="gst"
              control={<Radio />}
              label={<Typography sx={{ fontSize: "14px" }}>GST</Typography>}
            />
            <FormControlLabel
              value="bank"
              control={<Radio />}
              label={<Typography sx={{ fontSize: "14px" }}>Bank</Typography>}
            />
            <FormControlLabel
              value="msme"
              control={<Radio />}
              label={<Typography sx={{ fontSize: "14px" }}>MSME</Typography>}
            />
            <FormControlLabel
              value="mca"
              control={<Radio />}
              label={<Typography sx={{ fontSize: "14px" }}>CIN</Typography>}
            />
          </RadioGroup>
        </FormControl>

        {/* Drag & Drop File Upload */}
          {/* Drag & Drop */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: "center",
              border: "2px dashed #aaa",
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
              maxWidth: "350px",
              height: "150px",
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current.click()}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center", 
                alignItems: "center", 
                height: "100%", 
                textAlign: "center",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 500 }}>
                Drag and drop file here
              </Typography>
              <Typography variant="h6" color="textSecondary">
                (Supported format excel / csv)
              </Typography>
            </div>

            <input
              type="file"
              accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </Paper>
           {/* file name */}
          {file && (
            <div style={{ marginTop: "0.8rem" }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setFile(null)}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  // gap: "8px",
                  width: "100%",
                  maxWidth: "110px",
                  padding: "1px 10px",
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                    flexGrow: 1,
                  }}
                  title={file.name}
                >
                  {file.name}
                </span>
                <DeleteForeverIcon fontSize="large" sx={{ color: "#FF0000" }} />
              </Button>
            </div>
          )}
      </Box>
       

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          marginLeft: "18px",
        }}
      >
        <Button
          variant="outlined"
          sx={{ mb: 2, fontSize: "10px" }}
          onClick={handleSampleDownload}
          disabled={!documentType}
        >
          Download Sample CSV
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            padding: "10px",
            fontSize: "10px",
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Start Verification"}
        </Button>
      </div>

      <div>
        <BulkVerificationTable
          data={bulkRecords}
          isLoading={tableProps.isLoading}
          errorMessage={tableProps.errorMessage}
          searchQuery={searchQuery}
          onSearchChange={(q) => {
            setSearchQuery(q);
            setPageNumber(1);
          }}
          filterValue={filterQuery}
          filterOptions={filterOptions}
          onFilterChange={(q) => {
            setFilterQuery(q);
            setPageNumber(1);
          }}
          pageNumber={pageNumber}
          paginationData={paginationData}
          setPageNumber={setPageNumber}
        />
      </div>

      <Modal open={successOpen} onClose={() => setSuccessOpen(false)}>
        <Fade in={successOpen}>
          <Box>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText="Extraction completed"
              popupDescription="Your file has been processed successfully."
              popBtn={true}
              buyerBtn={true}
              btnText="Okay"
              btnClick={() => setSuccessOpen(false)}
            />
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default VendorVerificationPartial;
